import { SETTINGS_LIMITS, STAFF_LIST_MAX } from '../speedToLead/settings.js';
import { SETUP_STEP_LABELS, type SetupResult, type SetupStatus, type SetupStepId } from './setupStatus.js';

// THE CLUB'S VIEW OF THE SETUP CHECKLIST (KyckConnect Phase 4). PURE.
//
// ⚠️ ONE statuses function (`computeSetup`) still decides whether a club is set up; this file only DECIDES WHAT THE CLUB IS SHOWN and
// what it may change. The Kyck-only steps (Number and call-backs, Test and go live) are ABSENT from the portal's step list, not
// hidden by the page, so nothing in the response can leak what the page chooses not to draw. The server enforces the same table on
// every write (strict schemas whose keys are exactly the club-editable ones), so the UI being read-only is a convenience.

/** The steps the club ever sees, in order. */
export const PORTAL_SETUP_STEP_IDS = ['services', 'profile', 'relay', 'aiSms', 'speedToLead'] as const satisfies readonly SetupStepId[];
export type PortalSetupStepId = (typeof PORTAL_SETUP_STEP_IDS)[number];

/**
 * Which steps the club can edit (with the `kyckconnect_setup` permission). `services` and `relay` are read-only cards. `aiSms` is the
 * offers the assistant may mention, and nothing else on the bot (name, instructions, mode, capabilities and caps are Kyck's).
 */
export const PORTAL_STEP_EDITABLE: Record<PortalSetupStepId, boolean> = {
  services: false,
  profile: true,
  relay: false,
  aiSms: true,
  speedToLead: true,
};

export const PORTAL_OFF_MESSAGE = 'Off. Talk to Kyck to turn this on.';
export const PORTAL_READ_ONLY_NOTE = 'You can see this setup but not change it. Ask Kyck to give you setup access.';

/** The `?step=` values of the portal page (the staff tab's values, so a shared link means the same step). */
export const PORTAL_SETUP_STEP_QUERY: Record<PortalSetupStepId, string> = {
  services: 'services',
  profile: 'profile',
  relay: 'relay',
  aiSms: 'ai-sms',
  speedToLead: 'speed-to-lead',
};
export function portalSetupStepFromQuery(value: string | null | undefined): PortalSetupStepId | null {
  return PORTAL_SETUP_STEP_IDS.find((id) => PORTAL_SETUP_STEP_QUERY[id] === value) ?? null;
}

/**
 * What a club may type into the Speed to Lead step, tighter than Kyck's own limits (the club can reach a lead faster or slower, but
 * not set a ring so short nobody can answer, or a gap so long the second round never happens).
 */
export const PORTAL_CALL_BOUNDS = {
  staffRingSeconds: { min: 10, max: 40 },
  /** 1 to 15 minutes, held as seconds like the stored field. */
  roundTwoDelaySeconds: { min: 60, max: 900 },
  retryAfterMinutes: SETTINGS_LIMITS.retryAfterMinutes,
} as const;

export interface PortalStepView {
  id: PortalSetupStepId;
  label: string;
  status: SetupStatus;
  detail: string;
  visible: boolean;
  /** The sentence shown in place of a step whose service is off ("Off. Talk to Kyck to turn this on."). */
  hiddenMessage: string | null;
  /** True when the club can change this step (it holds the permission AND the step is a shared one). */
  editable: boolean;
  /** True on steps that are club-editable in principle: the "Shared" tag. */
  shared: boolean;
}

/**
 * The setup result, as the club sees it. ⚠️ Built field by field from the shared result: the pool, the bot's number, the warnings, the
 * go-live checks and the two Kyck-only steps are not carried over.
 */
export function portalStepsFrom(result: SetupResult, canEdit: boolean): PortalStepView[] {
  return PORTAL_SETUP_STEP_IDS.map((id) => {
    const step = result.steps.find((s) => s.id === id);
    if (!step) throw new Error(`computeSetup returned no ${id} step`);
    return {
      id,
      label: SETUP_STEP_LABELS[id],
      status: step.status,
      detail: step.detail,
      visible: step.visible,
      hiddenMessage: step.visible ? null : PORTAL_OFF_MESSAGE,
      editable: canEdit && PORTAL_STEP_EDITABLE[id] && step.visible,
      shared: PORTAL_STEP_EDITABLE[id],
    };
  });
}

/** The club's Speed to Lead editable fields. Kyck-only fields are not in this type, so they cannot be sent. */
export interface PortalSpeedToLeadInput {
  staffList: Array<{ teamMemberId: string; receivesLeadCalls: boolean }>;
  staffRingSeconds: number;
  roundTwoDelaySeconds: number;
  retryAfterMinutes: number | null;
  whisperMessage?: string | null;
  missedCallMessage?: string | null;
  outboundWhisperMessage?: string | null;
}

export interface StoredCallNumbers {
  staffRingSeconds: number;
  roundTwoDelaySeconds: number;
  retryAfterMinutes: number | null;
}

export type PortalBoundsVerdict = { ok: true } | { ok: false; error: string };

/**
 * The bounds a CLUB write must meet. ⚠️ A value equal to the stored one always passes: Kyck may have set a ring time outside the
 * club's range, and saving the form unchanged must not fail on a number the club never touched. A CHANGED value must be in range.
 */
export function checkPortalCallBounds(input: PortalSpeedToLeadInput, stored: StoredCallNumbers): PortalBoundsVerdict {
  const b = PORTAL_CALL_BOUNDS;
  const whole = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);
  if (!whole(input.staffRingSeconds) || (input.staffRingSeconds !== stored.staffRingSeconds && (input.staffRingSeconds < b.staffRingSeconds.min || input.staffRingSeconds > b.staffRingSeconds.max))) {
    return { ok: false, error: `Ring each person for a whole number of seconds from ${b.staffRingSeconds.min} to ${b.staffRingSeconds.max}.` };
  }
  if (!whole(input.roundTwoDelaySeconds) || (input.roundTwoDelaySeconds !== stored.roundTwoDelaySeconds && (input.roundTwoDelaySeconds < b.roundTwoDelaySeconds.min || input.roundTwoDelaySeconds > b.roundTwoDelaySeconds.max))) {
    return { ok: false, error: `Wait before the second round for 1 to 15 minutes (${b.roundTwoDelaySeconds.min} to ${b.roundTwoDelaySeconds.max} seconds).` };
  }
  const r = input.retryAfterMinutes;
  if (r !== null && (!whole(r) || (r !== stored.retryAfterMinutes && (r < b.retryAfterMinutes.min || r > b.retryAfterMinutes.max)))) {
    return { ok: false, error: `Retry later after a whole number of minutes from ${b.retryAfterMinutes.min} to ${b.retryAfterMinutes.max}, or leave it empty.` };
  }
  if (input.staffList.length > STAFF_LIST_MAX) return { ok: false, error: `The call list can hold up to ${STAFF_LIST_MAX} people.` };
  return { ok: true };
}

/**
 * The status dot beside a step in the portal's left list (the Setup checklist board): a done step is a GREEN FILL, the step being
 * edited is the page's ONE volt element, an incomplete step is an outline, and an off step is a muted fill. ⚠️ `current` wins over the
 * status, so there is never more than one volt dot on the page.
 */
export type StepDot = 'done' | 'current' | 'incomplete' | 'off';
export function stepDot(status: SetupStatus, isCurrent: boolean): StepDot {
  if (isCurrent) return 'current';
  return status === 'done' ? 'done' : status === 'incomplete' ? 'incomplete' : 'off';
}
export const STEP_STATUS_WORDS: Record<SetupStatus, string> = { done: 'Done', incomplete: 'Needs attention', off: 'Off' };
